/**
 * `partialUpdate(schema)`: the schema for an UPDATE, where an absent key means
 * "leave this column alone".
 *
 * Why not `schema.partial()`: zod 3 skipped a field's `.default()` when the key
 * was absent from a partial schema. zod 4 applies it, so parsing
 * `{ visibleOnBallee: false }` against a partial dancer schema also returns
 * `isProfilePublic: false`, `availableForHire: true`, ... and an update writes
 * every one of them. `partialUpdate` removes the defaults first, then makes every
 * key optional, so a parsed update holds exactly the keys the caller sent.
 *
 * Defaults belong on the create schema only.
 */
import { z } from 'zod';

type AnyZod = z.ZodType;
type ZodDef = NonNullable<Parameters<AnyZod['clone']>[0]>;

/**
 * Unwraps defaults at every level that can hide one: optional, nullable, pipe,
 * nested object. A wrapper is cloned from its own definition, never rebuilt, so
 * checks chained on it (`.optional().refine(...)`, `.transform(...).refine(...)`)
 * survive.
 */
function withoutDefaults(schema: AnyZod): AnyZod {
  const def = schema._zod.def as { type: string; checks?: unknown[] } & Record<
    string,
    unknown
  >;

  switch (def.type) {
    case 'default':
    case 'prefault': {
      const inner = withoutDefaults(def.innerType as AnyZod);
      const own = def.checks ?? [];

      if (own.length === 0) {
        return inner;
      }

      // A check chained after .default() belongs to the value; carry it over.
      const innerDef = inner._zod.def as { checks?: unknown[] };

      return inner.clone({
        ...innerDef,
        checks: [...(innerDef.checks ?? []), ...own],
      } as ZodDef);
    }
    case 'optional':
    case 'nullable':
      return schema.clone({
        ...def,
        innerType: withoutDefaults(def.innerType as AnyZod),
      } as ZodDef);
    case 'pipe':
      return schema.clone({
        ...def,
        in: withoutDefaults(def.in as AnyZod),
      } as ZodDef);
    case 'object': {
      const object = schema as z.ZodObject;
      // A nested field that had a default was optional in the input; it stays
      // optional once the default is gone.
      return object.extend(
        stripShape(object.shape as Record<string, AnyZod>, true),
      );
    }
    default:
      return schema;
  }
}

function hasDefault(schema: AnyZod): boolean {
  const def = schema._zod.def as { type: string } & Record<string, unknown>;

  if (def.type === 'default' || def.type === 'prefault') {
    return true;
  }

  if (def.type === 'optional' || def.type === 'nullable') {
    return hasDefault(def.innerType as AnyZod);
  }

  return false;
}

function stripShape(
  shape: Record<string, AnyZod>,
  keepDefaultedOptional = false,
): Record<string, AnyZod> {
  return Object.fromEntries(
    Object.entries(shape).map(([key, field]) => {
      const stripped = withoutDefaults(field);

      return [
        key,
        keepDefaultedOptional && hasDefault(field)
          ? stripped.optional()
          : stripped,
      ];
    }),
  );
}

export function partialUpdate<T extends z.ZodObject>(
  schema: T,
): ReturnType<T['partial']> {
  const shape = stripShape(schema.shape as Record<string, AnyZod>);
  const optional = Object.fromEntries(
    Object.entries(shape).map(([key, field]) => [key, field.optional()]),
  );

  return schema.extend(optional) as unknown as ReturnType<T['partial']>;
}
